import { IconAlertTriangleFilled, IconCircleCheckFilled, IconInfoCircleFilled, IconX } from '@tabler/icons-react'
import { AnimatePresence, AnimationSequence, motion, useAnimate, Variants } from 'motion/react'
import { ReactNode, SyntheticEvent, useCallback, useEffect, useRef } from 'react'

import { AstraIcon } from '../AstraIcon/AstraIcon'
import { AstraButton } from '../Inputs/AstraButton/AstraButton'
import { AstraParagraph } from '../Typography/AstraParagraph/AstraParagraph'
import { astraTwMerge } from '../Utilities/astraTwMerge'

export type ToastLevelColors = 'default' | 'success' | 'critical' | 'info'

export type ToastProps = {
    id?: string
    /**
     * The color of the callout, depending on the nature or severity of the message displayed.
     * */
    color?: ToastLevelColors
    /**
     * The text to display in the callout
     */
    message: ReactNode
    /**
     * The optional action button to display in the toast
     * @default undefined
     * */
    action?: {
        label: string
        onClick: () => void
    }
    onClose?: (id?: string) => void
    /**
     * The duration in seconds before the toast closes automatically
     * @default 5
     * */
    toastTimeout?: number
    /**
     * Forces the toast to be persistent, meaning it won't close automatically
     * @default false
     */
    persist?: boolean
    /**
     * Controls which disappearance animation runs over the toast lifetime.
     * @default 'scale'
     */
    animation?: 'bar' | 'scale'
    size?: 'small' | 'medium'
    dataTestSuffix?: string
    noUnstableEffects?: boolean
}

const textStyles: Record<ToastLevelColors, string> = {
    default: 'text-default',
    success: 'text-success',
    critical: 'text-critical',
    info: 'text-info',
}

const ringStyles: Record<ToastLevelColors, string> = {
    default: 'hover:ring-strong',
    success: 'hover:ring-success-success-ring',
    critical: 'hover:ring-critical-ring',
    info: 'hover:ring-info-info-ring',
}

const iconByVariant: Record<ToastLevelColors, ReactNode> = {
    default: <AstraIcon icon={IconInfoCircleFilled} size="medium" color="text-muted" />,
    success: <AstraIcon icon={IconCircleCheckFilled} size="medium" color="text-success-emphasis" />,
    critical: <AstraIcon icon={IconAlertTriangleFilled} size="medium" color="text-critical-emphasis" />,
    info: <AstraIcon icon={IconInfoCircleFilled} size="medium" color="text-info-emphasis" />,
}

const toastVariants: Variants = {
    initial: { opacity: 0, y: 8 },
    animate: { opacity: 1, y: 0, transition: { type: 'spring', duration: 0.35, bounce: 0.25 } },
    exit: { opacity: 0, y: -4, transition: { duration: 0.18 } },
}

export const AstraToast = ({
    color = 'default',
    id,
    message,
    action,
    onClose,
    toastTimeout = 5,
    persist = false,
    animation = 'scale',
    size = 'small',
    dataTestSuffix,
    noUnstableEffects = false,
}: ToastProps) => {
    const resolvedIdRef = useRef(id ?? crypto.randomUUID())
    const resolvedId = resolvedIdRef.current
    const onCloseRef = useRef(onClose)
    const [scope, animate] = useAnimate()
    const progressRef = useRef<HTMLDivElement | null>(null)
    const animationRef = useRef<ReturnType<typeof animate> | null>(null)
    const dataTest = dataTestSuffix ? `hero-toast-${color}-${dataTestSuffix}` : `hero-toast-${color}`

    const schedule = useCallback(() => {
        if (persist || !onCloseRef.current) return
        const bar = progressRef.current
        const rootEl = scope.current
        if (!rootEl) return

        let steps: AnimationSequence = []
        if (animation === 'bar' && bar) {
            steps = [[bar, { scaleX: 0 }, { duration: toastTimeout, ease: 'linear' }]]
        } else if (animation === 'scale') {
            steps = [[rootEl, { scale: 0.95 }, { duration: toastTimeout, ease: 'linear' }]]
        }

        animationRef.current = animate(steps)
        void animationRef.current.then(() => {
            onCloseRef.current?.(resolvedId)
            animationRef.current = null
        })
    }, [animate, persist, toastTimeout, scope, animation, resolvedId])

    useEffect(() => {
        if (animationRef.current) {
            animationRef.current.stop()
            animationRef.current = null
        }
        schedule()
        return () => {
            if (animationRef.current) animationRef.current.stop()
        }
    }, [schedule, toastTimeout])

    useEffect(() => {
        onCloseRef.current = onClose
    }, [onClose])

    const pause = () => {
        if (persist) return
        animationRef.current?.pause()
    }

    const resume = () => {
        if (persist) return
        animationRef.current?.play()
    }

    const onMouseEnter = () => {
        pause()
    }

    const onMouseLeave = () => {
        resume()
    }

    const manualClose = () => {
        if (animationRef.current) {
            animationRef.current.stop()
            animationRef.current = null
        }
        onCloseRef.current?.(resolvedId)
    }

    return (
        <motion.div
            layout
            id={resolvedId}
            variants={toastVariants}
            initial="initial"
            animate="animate"
            exit="exit"
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
            ref={noUnstableEffects ? undefined : scope}
            role="status"
            aria-live="polite"
            className={astraTwMerge(
                'relative flex origin-center justify-between gap-2 overflow-hidden rounded-lg bg-surface-base px-[14px] py-4 shadow-md hover:ring-1',
                size === 'small' ? 'w-96' : '',
                size === 'medium' ? 'w-[720px]' : '',
                ringStyles[color]
            )}
            data-test={dataTest}
        >
            {!persist && animation === 'bar' && (
                <div className="bg-fill-neutral-subtle/30 pointer-events-none absolute bottom-0 left-0 right-0 h-0.5 origin-left will-change-transform">
                    <div ref={progressRef} className="h-full w-full origin-left bg-chart-divergent-lowmid" />
                </div>
            )}
            <div className="flex items-start gap-2">
                <div className="pt-0.5">{iconByVariant[color]}</div>
                <AstraParagraph className={textStyles[color]}>{message}</AstraParagraph>
            </div>
            <div className="flex h-fit items-start gap-1">
                {action && (
                    <AstraButton
                        size="small"
                        variant="secondary"
                        onClick={() => {
                            action.onClick()
                            manualClose()
                        }}
                        data-test="hero-toast-action-button"
                        label={action.label}
                    />
                )}
                {onClose && <AstraButton onClick={manualClose} icon={<AstraIcon icon={IconX} />} size="small" variant="ghost" />}
            </div>
        </motion.div>
    )
}

export type PLACEMENT_OPTIONS = 'top-left' | 'top-center' | 'top-right'
export type ToastAndPosition = {
    placement?: PLACEMENT_OPTIONS
} & ToastProps

const placementStyles: Record<PLACEMENT_OPTIONS, string> = {
    'top-left': 'top-2 left-52',
    'top-center': 'top-2 left-1/2 -translate-x-1/2',
    'top-right': 'top-2 right-52',
}

const placements = ['top-left', 'top-center', 'top-right'] as const

export type ToasterPlacementProps = {
    /**
     * The array of toasts to display
     * */
    toasts: ToastAndPosition[]
    /**
     * The function to call when a toast is closed
     * */
    onClose: (id: string) => void
    noUnstableEffects?: boolean
}

const stopPropagationHandler = (e: SyntheticEvent) => {
    e.stopPropagation()
}

export const ToasterPlacement = ({ toasts, onClose, noUnstableEffects }: ToasterPlacementProps) =>
    placements.map(p => (
        <div
            className={`absolute z-priority ${placementStyles[p]}`}
            key={p}
            onClick={stopPropagationHandler}
            onPointerDown={stopPropagationHandler}
            onMouseDown={stopPropagationHandler}
        >
            <div className="absolute mt-3 flex w-full flex-col items-center space-y-3">
                <AnimatePresence mode="sync">
                    {toasts
                        .filter(t => t.placement === p)
                        .map(toast => (
                            <AstraToast {...toast} key={toast.id} onClose={() => onClose(toast.id ?? '1')} noUnstableEffects={noUnstableEffects} />
                        ))}
                </AnimatePresence>
            </div>
        </div>
    ))
